// Parses "#rrggbb" or "rgba(r,g,b,a)" into {r,g,b,a} (0-255 channels, a in [0,1]).
function parseColor(input) {
  if (input.startsWith("#")) {
    const hex = input.slice(1);
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: 1,
    };
  }
  const match = input.match(/rgba?\(([^)]+)\)/);
  const [r, g, b, a = 1] = match[1].split(",").map((n) => parseFloat(n));
  return { r, g, b, a };
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Interpolates two colors (any parseColor-accepted format) and always returns
// an rgba() string, so the caller never needs to know which input format won.
function lerpColor(colorA, colorB, t) {
  const a = parseColor(colorA);
  const b = parseColor(colorB);
  const r = Math.round(lerp(a.r, b.r, t));
  const g = Math.round(lerp(a.g, b.g, t));
  const bl = Math.round(lerp(a.b, b.b, t));
  const al = Number(lerp(a.a, b.a, t).toFixed(3));
  return `rgba(${r}, ${g}, ${bl}, ${al})`;
}

// Keyframe data — hex/rgba values match docs/superpowers/specs/2026-07-19-theme-slider-design.md.
// `bg` is always 3 gradient stops (Dusk/Noon flattened to a repeated solid color at the
// same 0%/52%/100% positions as Dawn's real gradient, so each stop interpolates independently
// and the gradient morphs smoothly rather than crossfading two flat images).
// `bgSolid` is a SEPARATE single representative color per keyframe (Dawn uses its gradient's
// middle 52% stop) — used anywhere a plain color is needed instead of a gradient function
// (Tailwind gradient-stop utilities can't accept a nested gradient as a stop color).
const KEYFRAMES = {
  0: {
    // Dusk
    bg: ["#0A0B10", "#0A0B10", "#0A0B10"],
    bgSolid: "#0A0B10",
    surface: "#12141D",
    accent: "#D4AF37",
    text: "#FDFBF7",
    text2: "#A1A1AA",
    glassBg: "rgba(255,255,255,0.03)",
    glassBorder: "rgba(255,255,255,0.08)",
    heroFilter: { brightness: 1, saturate: 1, sepia: 0, contrast: 1 },
  },
  50: {
    // Dawn
    bg: ["#E5D2A2", "#DDBF7B", "#D5A653"],
    bgSolid: "#DDBF7B",
    surface: "rgba(255,250,236,0.30)",
    accent: "#C9A22E",
    text: "#33250F",
    text2: "#6F5A2D",
    glassBg: "rgba(28,22,16,0.04)",
    glassBorder: "rgba(28,22,16,0.10)",
    // A light brightness/warmth lift per the design spec — exact numbers are a starting
    // point sitting between Dusk's no-op and Noon's stronger lift; tune visually against
    // the real hero photo during manual QA (Task 6, spec's "Hero photo" note).
    heroFilter: { brightness: 1.12, saturate: 1.03, sepia: 0.05, contrast: 1.03 },
  },
  100: {
    // Noon
    bg: ["#EFE8DA", "#EFE8DA", "#EFE8DA"],
    bgSolid: "#EFE8DA",
    surface: "#F7F2E7",
    accent: "#A8781F",
    text: "#1C1610",
    text2: "#6B6155",
    glassBg: "rgba(28,22,16,0.04)",
    glassBorder: "rgba(28,22,16,0.10)",
    heroFilter: { brightness: 1.3, saturate: 1.05, sepia: 0.12, contrast: 1.08 },
  },
};

// Returns [fromKeyframe, toKeyframe, localT] for a 0-100 position — localT is the
// 0-1 fraction WITHIN the chosen segment (Dusk->Dawn or Dawn->Noon), not the raw position.
function getSegment(position) {
  const p = Math.max(0, Math.min(100, position));
  if (p <= 50) {
    return [KEYFRAMES[0], KEYFRAMES[50], p / 50];
  }
  return [KEYFRAMES[50], KEYFRAMES[100], (p - 50) / 50];
}

function lerpHeroFilter(from, to, t) {
  const brightness = lerp(from.brightness, to.brightness, t);
  const saturate = lerp(from.saturate, to.saturate, t);
  const sepia = lerp(from.sepia, to.sepia, t);
  const contrast = lerp(from.contrast, to.contrast, t);
  return `brightness(${brightness.toFixed(3)}) saturate(${saturate.toFixed(3)}) sepia(${sepia.toFixed(3)}) contrast(${contrast.toFixed(3)})`;
}

export function getInterpolatedTheme(position) {
  const [from, to, t] = getSegment(position);
  const bgStops = from.bg.map((stopFrom, i) => lerpColor(stopFrom, to.bg[i], t));
  return {
    bg: `linear-gradient(165deg, ${bgStops[0]} 0%, ${bgStops[1]} 52%, ${bgStops[2]} 100%)`,
    bgSolid: lerpColor(from.bgSolid, to.bgSolid, t),
    surface: lerpColor(from.surface, to.surface, t),
    accent: lerpColor(from.accent, to.accent, t),
    text: lerpColor(from.text, to.text, t),
    text2: lerpColor(from.text2, to.text2, t),
    glassBg: lerpColor(from.glassBg, to.glassBg, t),
    glassBorder: lerpColor(from.glassBorder, to.glassBorder, t),
    heroFilter: lerpHeroFilter(from.heroFilter, to.heroFilter, t),
  };
}

export { lerp, lerpColor };
